import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import { mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { SITE_URL } from '@/constants/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  // Fallback if article not found
  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-background-200 flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-8">The guide you are looking for does not exist or may have been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i> Back to Homepage
            </Link>
          </div>
        </main>
        <FooterSection />
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/guide/${article.slug}`;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Organization',
              name: 'VigorBuy',
              url: SITE_URL,
            },
            datePublished: article.publishDate,
            dateModified: article.publishDate,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': articleUrl,
            },
            publisher: {
              '@type': 'Organization',
              name: 'VigorBuy',
              url: SITE_URL,
            },
          }),
        }}
      />

      {/* Breadcrumbs Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: SITE_URL,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.title,
                item: articleUrl,
              },
            ],
          }),
        }}
      />

      <main className="pt-24 pb-16">
        <article className="max-w-3xl mx-auto px-4 md:px-6">
          {/* Breadcrumbs */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer font-medium">
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-foreground-400">{article.category}</span>
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-primary-600 bg-primary-50 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span>Updated: {new Date(article.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime}</span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-10 rounded-2xl overflow-hidden bg-background-100">
            <img
              src={article.featuredImage}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
              loading="eager"
            />
          </div>

          {/* Article Content */}
          <div className="text-foreground-800 leading-relaxed">
            {/* Introduction paragraph is always first */}
            {article.sections.map((section, index) => {
              if (section.type === 'heading') {
                return (
                  <h2 key={index} className="font-heading text-2xl font-bold text-foreground-950 mt-10 mb-4">
                    {section.heading}
                  </h2>
                );
              }
              if (section.type === 'paragraph') {
                return (
                  <p key={index} className="mb-4 text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.content || '' }} />
                );
              }
              if (section.type === 'list') {
                return (
                  <ul key={index} className="list-disc pl-6 mb-4 space-y-2 text-foreground-700">
                    {section.items?.map((item, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                );
              }
              return null;
            })}
          </div>

          {/* CTA */}
          <div className="mt-10 p-8 bg-background-100 rounded-2xl border-2 border-background-200/70 text-center">
            <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
              Ready to Start Exploring?
            </h3>
            <p className="text-foreground-600 text-sm mb-6">
              Access the full VigorBuy Spreadsheet and discover hundreds of verified products across all categories.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 text-sm font-bold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                VigorBuy Sheet
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-6 py-3 text-sm font-semibold text-foreground-700 border-2 border-foreground-300 rounded-full hover:border-foreground-500 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t-2 border-background-200/70">
            <h4 className="font-heading text-sm font-bold text-foreground-500 uppercase tracking-wide mb-4">
              Related Resources
            </h4>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 hover:text-foreground-950 transition-all cursor-pointer"
              >
                VigorBuy Homepage
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 hover:text-foreground-950 transition-all cursor-pointer"
              >
                Main Website
              </a>
              <Link
                to="/#categories"
                className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 hover:text-foreground-950 transition-all cursor-pointer"
              >
                Product Categories
              </Link>
              {articles.filter(a => a.slug !== slug).slice(0, 3).map((a) => (
                <Link
                  key={a.slug}
                  to={`/guide/${a.slug}`}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-100 rounded-full hover:bg-background-200 hover:text-foreground-950 transition-all cursor-pointer"
                >
                  {a.title.length > 40 ? `${a.title.slice(0, 40)}...` : a.title}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <FooterSection />
    </div>
  );
}